import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { SectionHeader } from "../shared/SectionHeader";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";

const SEAT_LIMITS: Record<string, number> = { free: 1, pro_solo: 5, pro_growth: 10 };

export function TeamAccess() {
  const { profile, businessId } = useAuth();
  const [seatsUsed, setSeatsUsed] = useState(1);
  const [loading, setLoading] = useState(true);
  const plan = (profile?.subscription_plan ?? "free") as string;
  const limit = SEAT_LIMITS[plan] ?? 1;
  const atLimit = seatsUsed >= limit;

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .eq("business_id", businessId)
      .then(({ count }) => {
        setSeatsUsed(count ?? 1);
        setLoading(false);
      });
  }, [businessId]);

  return (
    <>
      <SectionHeader
        title="Team & Access"
        description="Invite team members and manage roles."
      />
      <div className="mb-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          <p className="text-[13px] text-muted-foreground">
            {loading ? "Loading…" : `${seatsUsed} / ${limit} seats used`}
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/team">
              <Users className="h-4 w-4 mr-1.5" />
              Manage Team
            </Link>
          </Button>
        </div>
        <Progress value={loading ? 0 : (seatsUsed / limit) * 100} className="h-2 mt-1" />
        {atLimit && !loading && (
          <p className="text-[11px] font-mono text-destructive mt-1">
            Seat limit reached — upgrade to add more members
          </p>
        )}
      </div>
      <p className="text-[12px] text-muted-foreground mb-4">
        Invite members and manage roles on the Team page. Permissions matrix below.
      </p>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left py-2 font-semibold text-foreground">Permission</th>
              <th className="text-left py-2 font-semibold text-foreground">Admin</th>
              <th className="text-left py-2 font-semibold text-foreground">Employee</th>
            </tr>
          </thead>
          <tbody className="text-foreground">
            {[
              ["View live stock levels", "✓", "✓"],
              ["Record sales", "✓", "✓"],
              ["Log expenses", "✓", "✓"],
              ["Add / edit products", "✓", "—"],
              ["View reports & P&L", "✓", "—"],
              ["Run BI Analysis", "✓", "—"],
              ["Manage suppliers", "✓", "—"],
              ["Invite / remove team members", "✓", "—"],
              ["Manage locations (branches)", "✓", "—"],
              ["Access billing & subscription", "✓", "—"],
              ["Export data & reports", "✓", "—"],
              ["View system logs", "✓", "—"],
            ].map(([perm, admin, emp], i) => (
              <tr key={i} className="border-b border-border last:border-b-0">
                <td className="py-2 text-muted-foreground">{perm}</td>
                <td className="py-2 text-foreground">{admin}</td>
                <td className="py-2">{emp === "✓" ? <span className="text-primary">✓</span> : <span className="text-muted-foreground">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
